import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const SESSION_KEY = "torvi_chat_session";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = "sess-" + Math.random().toString(36).slice(2) + Date.now();
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  });
  const [messages, setMessages] = useState([
    { role: "assistant", content: "¡Hola! Soy Torvi Bot ✨ ¿Te ayudo a personalizar tu funda en 15 minutos?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((p) => [...p, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: msg });
      setMessages((p) => [...p, { role: "assistant", content: data.response }]);
    } catch {
      setMessages((p) => [...p, { role: "assistant", content: "Disculpa, tuve un problema. Escríbenos al WhatsApp +56 9 7535 9774 ✨" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 pl-3 pr-4 h-14 rounded-full bg-[#0EA5E9] hover:bg-sky-400 text-white tech-glow transition ${open ? "scale-0 opacity-0" : "scale-100 opacity-100"}`}
        data-testid="chatbot-open"
        aria-label="Abrir chat"
      >
        <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center backdrop-blur">
          <MessageCircle size={18} />
        </span>
        <span className="font-heading font-black text-sm pr-1">Te ayudo</span>
      </button>

      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-3xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden" data-testid="chatbot-panel">
          <div className="px-4 py-3 bg-gradient-to-r from-[#0EA5E9] to-sky-400 text-white flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center backdrop-blur">
                <Sparkles size={16} />
              </div>
              <div>
                <div className="font-heading font-black leading-none">Torvi Bot</div>
                <div className="text-xs opacity-90 mt-0.5">En línea · Responde rápido</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center" data-testid="chatbot-close">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-black text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-slate-500">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay: '0ms'}} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay: '150ms'}} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay: '300ms'}} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-3 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Escribe tu pregunta..."
              data-testid="chatbot-input"
              className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/30 outline-none"
            />
            <button onClick={send} disabled={loading} className="w-10 h-10 rounded-full bg-[#0EA5E9] hover:bg-sky-400 text-white flex items-center justify-center disabled:opacity-50" data-testid="chatbot-send">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
